/* Pure layout helpers behind the designer's Arrange toolbar: align, distribute,
 * center, match size, auto-arrange and grid density. All units are grid cells of
 * the page's own canvas (page.canvas.columns x rowsFor(page)), and filter-bar
 * widgets (isBarWidget) are never moved -- they don't live on the grid. */
import { GRID, isBarWidget, rowsFor, type SLayout, type SPage, type SWidget } from "./model";

export type AlignMode = "left" | "center" | "right" | "top" | "middle" | "bottom";
export type Axis = "h" | "v";

export type Density = "standard" | "fine";
export const DENSITY_COLUMNS: Record<Density, number> = { standard: 24, fine: 48 };

const colsOf = (page: SPage) => page.canvas?.columns ?? GRID.columns;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const overlaps = (a: SLayout, b: SLayout) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

function onGrid(page: SPage, ids: string[]): SWidget[] {
  // locked visuals (widget.locked) never move through the arrange tools
  return page.widgets.filter((w) => ids.includes(w.id) && !isBarWidget(w) && !w.locked);
}

function patch(page: SPage, changes: Map<string, Partial<SWidget["layout"]>>): SPage {
  if (changes.size === 0) return page;
  return {
    ...page,
    widgets: page.widgets.map((w) => {
      const c = changes.get(w.id);
      return c ? { ...w, layout: { ...w.layout, ...c } } : w;
    }),
  };
}

export function densityOf(page: SPage): Density | "custom" {
  const cols = page.canvas?.columns ?? GRID.columns;
  if (cols === DENSITY_COLUMNS.standard) return "standard";
  if (cols === DENSITY_COLUMNS.fine) return "fine";
  return "custom";
}

/** Cells per standard cell on this page (1 on the standard grid, 2 on fine). */
export function scaleOf(page: SPage): number {
  return Math.max(1, Math.round(colsOf(page) / GRID.columns));
}

/** Align the selection to each other (2+), or one widget to the page. */
export function align(page: SPage, ids: string[], mode: AlignMode): SPage {
  const t = onGrid(page, ids);
  if (t.length === 0) return page;
  const cols = colsOf(page);
  const rows = rowsFor(page);
  const toPage = t.length === 1;
  const minX = toPage ? 0 : Math.min(...t.map((w) => w.layout.x));
  const maxX = toPage ? cols : Math.max(...t.map((w) => w.layout.x + w.layout.w));
  const minY = toPage ? 0 : Math.min(...t.map((w) => w.layout.y));
  const maxY = toPage ? rows : Math.max(...t.map((w) => w.layout.y + w.layout.h));
  const out = new Map<string, Partial<SWidget["layout"]>>();
  for (const w of t) {
    const l = w.layout;
    if (mode === "left") out.set(w.id, { x: minX });
    else if (mode === "right") out.set(w.id, { x: clamp(maxX - l.w, 0, cols - l.w) });
    else if (mode === "center") out.set(w.id, { x: clamp(minX + Math.floor((maxX - minX - l.w) / 2), 0, cols - l.w) });
    else if (mode === "top") out.set(w.id, { y: minY });
    else if (mode === "bottom") out.set(w.id, { y: Math.max(0, maxY - l.h) });
    else out.set(w.id, { y: Math.max(0, minY + Math.floor((maxY - minY - l.h) / 2)) });
  }
  return patch(page, out);
}

/** Equal space between 3+ widgets along an axis (outermost two stay put). */
export function distribute(page: SPage, ids: string[], axis: Axis): SPage {
  const t = onGrid(page, ids);
  if (t.length < 3) return page;
  const pos = (w: SWidget) => (axis === "h" ? w.layout.x : w.layout.y);
  const size = (w: SWidget) => (axis === "h" ? w.layout.w : w.layout.h);
  const sorted = [...t].sort((a, b) => pos(a) - pos(b));
  const start = pos(sorted[0]);
  const end = Math.max(...sorted.map((w) => pos(w) + size(w)));
  const free = end - start - sorted.reduce((s, w) => s + size(w), 0);
  const slots = sorted.length - 1;
  const out = new Map<string, Partial<SWidget["layout"]>>();
  let cursor = start;
  sorted.forEach((w, i) => {
    out.set(w.id, axis === "h" ? { x: cursor } : { y: cursor });
    // spread the leftover cells so gaps differ by at most one cell
    const gap = Math.floor(free / slots) + (i < free % slots ? 1 : 0);
    cursor += size(w) + gap;
  });
  return patch(page, out);
}

/** Move the selection (or, with no selection, everything on the grid) as a group so it is centred on the page. */
export function centerOnPage(page: SPage, ids: string[], axis: Axis): SPage {
  const t = ids.length ? onGrid(page, ids) : page.widgets.filter((w) => !isBarWidget(w));
  if (t.length === 0) return page;
  const out = new Map<string, Partial<SWidget["layout"]>>();
  if (axis === "h") {
    const cols = colsOf(page);
    const min = Math.min(...t.map((w) => w.layout.x));
    const max = Math.max(...t.map((w) => w.layout.x + w.layout.w));
    const shift = Math.floor((cols - (max - min)) / 2) - min;
    t.forEach((w) => out.set(w.id, { x: clamp(w.layout.x + shift, 0, cols - w.layout.w) }));
  } else {
    const rows = rowsFor(page);
    const min = Math.min(...t.map((w) => w.layout.y));
    const max = Math.max(...t.map((w) => w.layout.y + w.layout.h));
    const shift = Math.floor((rows - (max - min)) / 2) - min;
    t.forEach((w) => out.set(w.id, { y: Math.max(0, w.layout.y + shift) }));
  }
  return patch(page, out);
}

/** Give the selection the same width and/or height as the primary (first-selected) widget. */
export function matchSize(page: SPage, ids: string[], dim: "w" | "h" | "both"): SPage {
  const t = onGrid(page, ids);
  if (t.length < 2) return page;
  const ref = page.widgets.find((w) => w.id === ids[0] && !isBarWidget(w)) ?? t[0];
  const cols = colsOf(page);
  const out = new Map<string, Partial<SWidget["layout"]>>();
  for (const w of t) {
    const c: Partial<SWidget["layout"]> = {};
    if (dim !== "h") {
      c.w = ref.layout.w;
      c.x = clamp(w.layout.x, 0, cols - ref.layout.w);
    }
    if (dim !== "w") c.h = ref.layout.h;
    out.set(w.id, c);
  }
  return patch(page, out);
}

/** Largest-remainder split of `total` cells in proportion to `weights` (each at least `min`). */
function apportion(weights: number[], total: number, min: number): number[] {
  const sum = weights.reduce((a, b) => a + b, 0) || 1;
  const raw = weights.map((w) => (w * total) / sum);
  const out = raw.map((r) => Math.max(min, Math.floor(r)));
  let left = total - out.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => [r - Math.floor(r), i] as const).sort((a, b) => b[0] - a[0]);
  for (let k = 0; left > 0; k = (k + 1) % order.length, left--) out[order[k][1]]++;
  for (let k = order.length - 1; left < 0 && k >= 0; left++) {
    const i = order[k][1];
    if (out[i] > min) out[i]--;
    else k--;
  }
  return out;
}

/** Re-flow every grid widget into tidy rows (reading order = top-to-bottom, left-to-right).
 *  keep: sizes unchanged, each row centred.   fill: each row stretched to the full width, one height per row. */
export function tidyRows(page: SPage, mode: "keep" | "fill"): SPage {
  const cols = colsOf(page);
  const items = page.widgets.filter((w) => !isBarWidget(w)).sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x);
  if (items.length === 0) return page;
  const rowsOfItems: SWidget[][] = [];
  let row: SWidget[] = [];
  let used = 0;
  for (const w of items) {
    const width = Math.min(w.layout.w, cols);
    if (row.length && used + width > cols) {
      rowsOfItems.push(row);
      row = [];
      used = 0;
    }
    row.push(w);
    used += width;
  }
  if (row.length) rowsOfItems.push(row);

  const min = 2;
  const out = new Map<string, Partial<SWidget["layout"]>>();
  let y = 0;
  for (const r of rowsOfItems) {
    const h = Math.max(...r.map((w) => w.layout.h));
    if (mode === "fill") {
      const widths = apportion(r.map((w) => Math.min(w.layout.w, cols)), cols, min);
      let x = 0;
      r.forEach((w, i) => {
        out.set(w.id, { x, y, w: widths[i], h });
        x += widths[i];
      });
    } else {
      const total = r.reduce((s, w) => s + Math.min(w.layout.w, cols), 0);
      let x = Math.floor((cols - total) / 2);
      for (const w of r) {
        const width = Math.min(w.layout.w, cols);
        out.set(w.id, { x, y, w: width });
        x += width;
      }
    }
    y += h;
  }
  return patch(page, out);
}

/** Tidy a layout WITHOUT changing its structure: edges that are almost lined up are made exactly equal
 *  (left/right/top/bottom, including the page edges), and small empty gaps between neighbouring visuals
 *  are closed by stretching the first one. Nothing is reordered, moved to another row or shrunk below a
 *  minimum, and any change that would make two visuals overlap is skipped. */
export function straighten(page: SPage): SPage {
  const k = scaleOf(page);
  const tolX = Math.max(1, Math.round(1.5 * k));
  const tolY = Math.max(1, Math.round(2 * k));
  const cols = colsOf(page);
  const min = 2;
  const items = page.widgets.filter((w) => !isBarWidget(w)).map((w) => ({ id: w.id, ...w.layout }));
  if (items.length < 2) return page;
  const hits = (a: SLayout, skipId: string) => items.some((o) => o.id !== skipId && overlaps(a, o));
  const apply = (it: (typeof items)[number], next: Partial<SLayout>) => {
    const cand = { x: it.x, y: it.y, w: it.w, h: it.h, ...next };
    if (cand.w < min || cand.h < min || cand.x < 0 || cand.x + cand.w > cols || cand.y < 0) return;
    if (hits(cand, it.id)) return;
    it.x = cand.x;
    it.y = cand.y;
    it.w = cand.w;
    it.h = cand.h;
  };

  // Map every nearly-equal edge value to its most common representative.
  const snapper = (values: number[], tol: number, anchors: number[]) => {
    const freq = new Map<number, number>();
    values.forEach((v) => freq.set(v, (freq.get(v) ?? 0) + 1));
    anchors.forEach((a) => freq.set(a, (freq.get(a) ?? 0) + 1000));
    const order = [...freq.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0]).map((e) => e[0]);
    const rep = new Map<number, number>();
    for (const anchor of order) {
      if (rep.has(anchor)) continue;
      for (const v of order) if (!rep.has(v) && Math.abs(v - anchor) <= tol) rep.set(v, anchor);
    }
    return (v: number) => rep.get(v) ?? v;
  };
  const rows = rowsFor(page);
  const sx = snapper(items.flatMap((i) => [i.x, i.x + i.w]), tolX, [0, cols]);
  for (const it of items) {
    const left = sx(it.x);
    const right = sx(it.x + it.w);
    if (left !== it.x || right !== it.x + it.w) apply(it, { x: left, w: right - left });
  }
  const sy = snapper(items.flatMap((i) => [i.y, i.y + i.h]), tolY, [0]);
  for (const it of items) {
    const top = sy(it.y);
    const bottom = sy(it.y + it.h);
    if (top !== it.y || bottom !== it.y + it.h) apply(it, { y: top, h: bottom - top });
  }

  // Close small gaps between neighbours (side by side, then stacked).
  const span = (a0: number, a1: number, b0: number, b1: number) => Math.min(a1, b1) - Math.max(a0, b0);
  for (const a of items) {
    let best: { gap: number } | null = null;
    for (const b of items) {
      if (a === b) continue;
      const gap = b.x - (a.x + a.w);
      if (gap > 0 && gap <= tolX && span(a.y, a.y + a.h, b.y, b.y + b.h) >= Math.min(a.h, b.h) / 2 && (!best || gap < best.gap)) best = { gap };
    }
    if (best) apply(a, { w: a.w + best.gap });
  }
  for (const a of items) {
    let best: { gap: number } | null = null;
    for (const b of items) {
      if (a === b) continue;
      const gap = b.y - (a.y + a.h);
      if (gap > 0 && gap <= tolY && span(a.x, a.x + a.w, b.x, b.x + b.w) >= Math.min(a.w, b.w) / 2 && (!best || gap < best.gap)) best = { gap };
    }
    if (best) apply(a, { h: a.h + best.gap });
  }
  void rows;
  const out = new Map<string, Partial<SLayout>>();
  for (const it of items) out.set(it.id, { x: it.x, y: it.y, w: it.w, h: it.h });
  return patch(page, out);
}

/** Pull every widget up as far as it can go without touching another (removes vertical gaps). */
export function compactUp(page: SPage, onlyIds?: string[]): SPage {
  const items = page.widgets.filter((w) => !isBarWidget(w)).sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x);
  const placed: SWidget[] = [];
  const out = new Map<string, Partial<SWidget["layout"]>>();
  for (const w of items) {
    let y = w.layout.y;
    if (!onlyIds || onlyIds.includes(w.id)) {
      while (y > 0) {
        const probe = { ...w.layout, y: y - 1 };
        if (placed.some((p) => overlaps(p.layout, probe))) break;
        y--;
      }
    }
    placed.push({ ...w, layout: { ...w.layout, y } });
    if (y !== w.layout.y) out.set(w.id, { y });
  }
  return patch(page, out);
}

/** Switch a page between the standard (24-column) and fine (48-column) grid, keeping every widget at
 *  exactly the same on-screen size and position. The page's own width, height and gap are kept; the row
 *  height is derived so the page does not grow: a fine row is (rowHeight - gap) / 2 tall, because two fine
 *  rows plus the gap between them equal one standard row. */
export function convertDensity(page: SPage, to: Density): { page: SPage; lossy: boolean } {
  const from = densityOf(page);
  if (from === to || from === "custom") return { page, lossy: false };
  const canvas = page.canvas ?? { width: GRID.width, height: GRID.height, columns: GRID.columns, rowHeight: GRID.rowHeight, gap: GRID.gap };
  const gap = canvas.gap;
  const factor = to === "fine" ? 2 : 0.5;
  let rowHeight: number;
  let lossy = false;
  const next: Record<string, any> = { ...canvas, columns: DENSITY_COLUMNS[to] };
  if (to === "fine") {
    rowHeight = Math.max(2, Math.round((canvas.rowHeight - gap) / 2));
    if ((canvas.rowHeight - gap) % 2 !== 0) lossy = true;
    next.stdRowHeight = canvas.rowHeight; // remembered so switching back is exact
  } else {
    const remembered = typeof canvas.stdRowHeight === "number" ? canvas.stdRowHeight : null;
    rowHeight = remembered !== null && Math.abs(2 * canvas.rowHeight + gap - remembered) <= 1 ? remembered : 2 * canvas.rowHeight + gap;
    delete next.stdRowHeight;
  }
  next.rowHeight = rowHeight;
  const scale = (v: number, floorTo = 0) => {
    const s = v * factor;
    if (!Number.isInteger(s)) lossy = true;
    return Math.max(floorTo, Math.round(s));
  };
  const converted: SPage = {
    ...page,
    canvas: next as SPage["canvas"],
    widgets: page.widgets.map((w) =>
      isBarWidget(w) ? w : { ...w, layout: { ...w.layout, x: scale(w.layout.x), y: scale(w.layout.y), w: scale(w.layout.w, 1), h: scale(w.layout.h, 1) } },
    ),
  };
  return { page: converted, lossy };
}
